// This file is auto-generated by @hey-api/openapi-ts

import { type Options, getHealth, getMe, listCustomers, listMachines, listMachineChecklists, listMachineChecklistRuns, listMachineChecklistDefinitionsManifest, listMachineChecklistDefinitionsExport, listChecklistVersionRuns, listChecklistRunsPaged, getChecklistRunDetails, getChecklistRunsFilterOptions, markChecklistRunRead, createChecklistRunSupportRequest, listChecklistRunItems, listChecklistRunOlderChecks, getChecklistRunItemMediaContent, getChecklistRunStatistics, importMachineChecklist } from '../sdk.gen';
import { queryOptions, infiniteQueryOptions, type InfiniteData, type UseMutationOptions } from '@tanstack/react-query';
import type { GetHealthData, GetMeData, ListCustomersData, ListMachinesData, ListMachineChecklistsData, ListMachineChecklistRunsData, ListMachineChecklistDefinitionsManifestData, ListMachineChecklistDefinitionsExportData, ListChecklistVersionRunsData, ListChecklistRunsPagedData, ListChecklistRunsPagedError, ListChecklistRunsPagedResponse, GetChecklistRunDetailsData, GetChecklistRunsFilterOptionsData, MarkChecklistRunReadData, MarkChecklistRunReadError, MarkChecklistRunReadResponse, CreateChecklistRunSupportRequestData, CreateChecklistRunSupportRequestError, CreateChecklistRunSupportRequestResponse, ListChecklistRunItemsData, ListChecklistRunOlderChecksData, GetChecklistRunItemMediaContentData, GetChecklistRunStatisticsData, ImportMachineChecklistData, ImportMachineChecklistError, ImportMachineChecklistResponse } from '../types.gen';
import { client as _heyApiClient } from '../client.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseUrl: (options?.client ?? _heyApiClient).getConfig().baseUrl } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [
        params
    ];
};

export const getHealthQueryKey = (options?: Options<GetHealthData>) => createQueryKey('getHealth', options);

/**
 * Health check
 */
export const getHealthOptions = (options?: Options<GetHealthData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getHealth({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getHealthQueryKey(options)
    });
};

export const getMeQueryKey = (options?: Options<GetMeData>) => createQueryKey('getMe', options);

/**
 * Get current session user
 */
export const getMeOptions = (options?: Options<GetMeData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getMe({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getMeQueryKey(options)
    });
};

export const listCustomersQueryKey = (options?: Options<ListCustomersData>) => createQueryKey('listCustomers', options);

/**
 * List customers for Langguth customer context (shop customer rows)
 * Allowed for langguth_admin and langguth_kd only. Customer users receive 403.
 */
export const listCustomersOptions = (options?: Options<ListCustomersData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listCustomers({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listCustomersQueryKey(options)
    });
};

export const listMachinesQueryKey = (options?: Options<ListMachinesData>) => createQueryKey('listMachines', options);

/**
 * List machines (shop customer_machine)
 * Customer users use session customer. Langguth admin/kd may omit X-Customer-Id to list machines across all customers, or set X-Customer-Id to scope to one customer.
 */
export const listMachinesOptions = (options?: Options<ListMachinesData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listMachines({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listMachinesQueryKey(options)
    });
};

export const listMachineChecklistsQueryKey = (options: Options<ListMachineChecklistsData>) => createQueryKey('listMachineChecklists', options);

/**
 * List checklists for a machine (current version metadata)
 */
export const listMachineChecklistsOptions = (options: Options<ListMachineChecklistsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listMachineChecklists({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listMachineChecklistsQueryKey(options)
    });
};

export const listMachineChecklistRunsQueryKey = (options: Options<ListMachineChecklistRunsData>) => createQueryKey('listMachineChecklistRuns', options);

/**
 * List checklist runs across all versions for one checklist on this machine
 * Same response shape as checklist-version runs, but includes rows for any checklist_version_id linked to this checklist (e.g. runs created before a definition republish bumped current_version_id).
 */
export const listMachineChecklistRunsOptions = (options: Options<ListMachineChecklistRunsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listMachineChecklistRuns({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listMachineChecklistRunsQueryKey(options)
    });
};

export const listMachineChecklistDefinitionsManifestQueryKey = (options: Options<ListMachineChecklistDefinitionsManifestData>) => createQueryKey('listMachineChecklistDefinitionsManifest', options);

/**
 * Lightweight manifest of checklists on a machine (for metered sync)
 * Same auth as definitions export (export API key or browser session with access to the machine's customer). Returns checklistId and maxVersionUpdatedAt (UTC) per checklist. Send If-None-Match with the ETag from a previous response to receive 304 with no body when nothing changed. GET may return Content-Encoding gzip when Accept-Encoding includes gzip and the payload is large enough.
 *
 */
export const listMachineChecklistDefinitionsManifestOptions = (options: Options<ListMachineChecklistDefinitionsManifestData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listMachineChecklistDefinitionsManifest({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listMachineChecklistDefinitionsManifestQueryKey(options)
    });
};

export const listMachineChecklistDefinitionsExportQueryKey = (options: Options<ListMachineChecklistDefinitionsExportData>) => createQueryKey('listMachineChecklistDefinitionsExport', options);

/**
 * Export portable YAML for checklist versions on a machine
 * Authenticates with the export shared secret (Authorization Bearer or X-Api-Key) or with the same browser session cookie as other checklist routes (user must be allowed to access the machine's shop customer). Customer is derived from the shop machine row; the machine must exist. Returns one array entry per checklist_version row matching the filters, ordered by checklist then version number. Optional updated_after returns only versions with updated_at after the timestamp (row-level delta). Optional checklist_id restricts to one checklist (404 if not on this machine). Send If-None-Match with the ETag from a prior response for 304 with no body when the snapshot is unchanged. Use Accept-Encoding gzip for smaller payloads on metered links. isCurrentVersion is true when this version is the checklist's current_version_id. YAML matches the import document shape (no customer or machine inside the document).
 *
 */
export const listMachineChecklistDefinitionsExportOptions = (options: Options<ListMachineChecklistDefinitionsExportData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listMachineChecklistDefinitionsExport({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listMachineChecklistDefinitionsExportQueryKey(options)
    });
};

export const listChecklistVersionRunsQueryKey = (options: Options<ListChecklistVersionRunsData>) => createQueryKey('listChecklistVersionRuns', options);

/**
 * List checklist runs for a version
 */
export const listChecklistVersionRunsOptions = (options: Options<ListChecklistVersionRunsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listChecklistVersionRuns({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listChecklistVersionRunsQueryKey(options)
    });
};

export const listChecklistRunsPagedQueryKey = (options?: Options<ListChecklistRunsPagedData>) => createQueryKey('listChecklistRunsPaged', options);

/**
 * List checklist runs (paged, sortable, filterable)
 * Customer users see only their session customer. Langguth admin/kd users may omit X-Customer-Id to list runs across all customers, or set X-Customer-Id to scope to one customer.
 */
export const listChecklistRunsPagedOptions = (options?: Options<ListChecklistRunsPagedData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listChecklistRunsPaged({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listChecklistRunsPagedQueryKey(options)
    });
};

const createInfiniteParams = <K extends Pick<QueryKey<Options>[0], 'body' | 'headers' | 'path' | 'query'>>(queryKey: QueryKey<Options>, page: K) => {
    const params = {
        ...queryKey[0]
    };
    if (page.body) {
        params.body = {
            ...queryKey[0].body as any,
            ...page.body as any
        };
    }
    if (page.headers) {
        params.headers = {
            ...queryKey[0].headers,
            ...page.headers
        };
    }
    if (page.path) {
        params.path = {
            ...queryKey[0].path as any,
            ...page.path as any
        };
    }
    if (page.query) {
        params.query = {
            ...queryKey[0].query as any,
            ...page.query as any
        };
    }
    return params as unknown as typeof page;
};

export const listChecklistRunsPagedInfiniteQueryKey = (options?: Options<ListChecklistRunsPagedData>): QueryKey<Options<ListChecklistRunsPagedData>> => createQueryKey('listChecklistRunsPaged', options, true);

/**
 * List checklist runs (paged, sortable, filterable)
 * Customer users see only their session customer. Langguth admin/kd users may omit X-Customer-Id to list runs across all customers, or set X-Customer-Id to scope to one customer.
 */
export const listChecklistRunsPagedInfiniteOptions = (options?: Options<ListChecklistRunsPagedData>) => {
    return infiniteQueryOptions<ListChecklistRunsPagedResponse, ListChecklistRunsPagedError, InfiniteData<ListChecklistRunsPagedResponse>, QueryKey<Options<ListChecklistRunsPagedData>>, number | Pick<QueryKey<Options<ListChecklistRunsPagedData>>[0], 'body' | 'headers' | 'path' | 'query'>>(
    // @ts-ignore
    {
        queryFn: async ({ pageParam, queryKey, signal }) => {
            // @ts-ignore
            const page: Pick<QueryKey<Options<ListChecklistRunsPagedData>>[0], 'body' | 'headers' | 'path' | 'query'> = typeof pageParam === 'object' ? pageParam : {
                query: {
                    page: pageParam
                }
            };
            const params = createInfiniteParams(queryKey, page);
            const { data } = await listChecklistRunsPaged({
                ...options,
                ...params,
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listChecklistRunsPagedInfiniteQueryKey(options)
    });
};

export const getChecklistRunDetailsQueryKey = (options: Options<GetChecklistRunDetailsData>) => createQueryKey('getChecklistRunDetails', options);

/**
 * Full checklist run details (list row plus items and media metadata)
 * Customer users are scoped to their session customer. Langguth admin/kd may omit X-Customer-Id; tenant is derived from the run id.
 */
export const getChecklistRunDetailsOptions = (options: Options<GetChecklistRunDetailsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getChecklistRunDetails({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getChecklistRunDetailsQueryKey(options)
    });
};

export const getChecklistRunsFilterOptionsQueryKey = (options?: Options<GetChecklistRunsFilterOptionsData>) => createQueryKey('getChecklistRunsFilterOptions', options);

/**
 * Distinct filter values for checklist runs (employees, machines, checklists with runs)
 * Scoped to X-Customer-Id when set for Langguth users; when omitted, options cover all customers.
 */
export const getChecklistRunsFilterOptionsOptions = (options?: Options<GetChecklistRunsFilterOptionsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getChecklistRunsFilterOptions({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getChecklistRunsFilterOptionsQueryKey(options)
    });
};

export const markChecklistRunReadQueryKey = (options: Options<MarkChecklistRunReadData>) => createQueryKey('markChecklistRunRead', options);

/**
 * Mark a checklist run as read for the current user
 */
export const markChecklistRunReadOptions = (options: Options<MarkChecklistRunReadData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await markChecklistRunRead({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: markChecklistRunReadQueryKey(options)
    });
};

/**
 * Mark a checklist run as read for the current user
 */
export const markChecklistRunReadMutation = (options?: Partial<Options<MarkChecklistRunReadData>>): UseMutationOptions<MarkChecklistRunReadResponse, MarkChecklistRunReadError, Options<MarkChecklistRunReadData>> => {
    const mutationOptions: UseMutationOptions<MarkChecklistRunReadResponse, MarkChecklistRunReadError, Options<MarkChecklistRunReadData>> = {
        mutationFn: async (localOptions) => {
            const { data } = await markChecklistRunRead({
                ...options,
                ...localOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const createChecklistRunSupportRequestQueryKey = (options: Options<CreateChecklistRunSupportRequestData>) => createQueryKey('createChecklistRunSupportRequest', options);

/**
 * Queue a HubSpot support ticket for this checklist run
 * Persists the request for asynchronous processing. Requires hubspot.api_key and checklisten.public_base_url on the server.
 */
export const createChecklistRunSupportRequestOptions = (options: Options<CreateChecklistRunSupportRequestData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await createChecklistRunSupportRequest({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: createChecklistRunSupportRequestQueryKey(options)
    });
};

/**
 * Queue a HubSpot support ticket for this checklist run
 * Persists the request for asynchronous processing. Requires hubspot.api_key and checklisten.public_base_url on the server.
 */
export const createChecklistRunSupportRequestMutation = (options?: Partial<Options<CreateChecklistRunSupportRequestData>>): UseMutationOptions<CreateChecklistRunSupportRequestResponse, CreateChecklistRunSupportRequestError, Options<CreateChecklistRunSupportRequestData>> => {
    const mutationOptions: UseMutationOptions<CreateChecklistRunSupportRequestResponse, CreateChecklistRunSupportRequestError, Options<CreateChecklistRunSupportRequestData>> = {
        mutationFn: async (localOptions) => {
            const { data } = await createChecklistRunSupportRequest({
                ...options,
                ...localOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const listChecklistRunItemsQueryKey = (options: Options<ListChecklistRunItemsData>) => createQueryKey('listChecklistRunItems', options);

/**
 * Per-item outcomes and media metadata for a checklist run
 */
export const listChecklistRunItemsOptions = (options: Options<ListChecklistRunItemsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listChecklistRunItems({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listChecklistRunItemsQueryKey(options)
    });
};

export const listChecklistRunOlderChecksQueryKey = (options: Options<ListChecklistRunOlderChecksData>) => createQueryKey('listChecklistRunOlderChecks', options);

/**
 * Matching checklist item instances from older runs (same machine and checklist) for history search
 * Tenant is derived from the run id. Returns item-level rows from prior runs ordered by completedAt descending. Requires a non-empty search string (title substring match); otherwise returns an empty list.
 */
export const listChecklistRunOlderChecksOptions = (options: Options<ListChecklistRunOlderChecksData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await listChecklistRunOlderChecks({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: listChecklistRunOlderChecksQueryKey(options)
    });
};

export const getChecklistRunItemMediaContentQueryKey = (options: Options<GetChecklistRunItemMediaContentData>) => createQueryKey('getChecklistRunItemMediaContent', options);

/**
 * Download binary media attached to a checklist item instance
 * Streams the object from configured object storage. Returns 503 when S3 is not configured.
 */
export const getChecklistRunItemMediaContentOptions = (options: Options<GetChecklistRunItemMediaContentData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getChecklistRunItemMediaContent({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getChecklistRunItemMediaContentQueryKey(options)
    });
};

export const getChecklistRunStatisticsQueryKey = (options?: Options<GetChecklistRunStatisticsData>) => createQueryKey('getChecklistRunStatistics', options);

/**
 * Completion KPIs for the dashboard (current customer)
 */
export const getChecklistRunStatisticsOptions = (options?: Options<GetChecklistRunStatisticsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getChecklistRunStatistics({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getChecklistRunStatisticsQueryKey(options)
    });
};

export const importMachineChecklistQueryKey = (options: Options<ImportMachineChecklistData>) => createQueryKey('importMachineChecklist', options);

/**
 * Import a checklist from YAML or JSON for a machine
 */
export const importMachineChecklistOptions = (options: Options<ImportMachineChecklistData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await importMachineChecklist({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: importMachineChecklistQueryKey(options)
    });
};

/**
 * Import a checklist from YAML or JSON for a machine
 */
export const importMachineChecklistMutation = (options?: Partial<Options<ImportMachineChecklistData>>): UseMutationOptions<ImportMachineChecklistResponse, ImportMachineChecklistError, Options<ImportMachineChecklistData>> => {
    const mutationOptions: UseMutationOptions<ImportMachineChecklistResponse, ImportMachineChecklistError, Options<ImportMachineChecklistData>> = {
        mutationFn: async (localOptions) => {
            const { data } = await importMachineChecklist({
                ...options,
                ...localOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};